import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Leaf, ArrowLeft, Download, Loader2, Sparkles, Wand2,
  Image as ImageIcon, Upload, X, Check, Camera
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMAGE_ZONE_RE = /\[\[IMAGE_ZONE:\s*(.*?)\s*\]\]/gi;

const MARGIN_PRESETS = {
  narrow: { id: "narrow", label: "Narrow", desc: "0.5\"", padding: "px-6 py-8" },
  standard: { id: "standard", label: "Standard", desc: "0.75\"", padding: "px-10 py-12" },
  wide: { id: "wide", label: "Wide", desc: "1.25\"", padding: "px-16 py-16" },
};

// Parse a chapter's content into [{type:"text"|"zone", text/hint, zoneIndex}]
function parseContent(content = "") {
  if (!content) return [];
  const parts = [];
  let lastEnd = 0;
  let zoneIdx = 0;
  IMAGE_ZONE_RE.lastIndex = 0;
  let m;
  while ((m = IMAGE_ZONE_RE.exec(content)) !== null) {
    const before = content.slice(lastEnd, m.index).trim();
    if (before) parts.push({ type: "text", text: before });
    parts.push({ type: "zone", hint: m[1], zoneIndex: zoneIdx++ });
    lastEnd = m.index + m[0].length;
  }
  const tail = content.slice(lastEnd).trim();
  if (tail) parts.push({ type: "text", text: tail });
  return parts;
}

const ZoneCard = ({ chapter, zone, choice, onSetChoice, hasChapterPhoto, chapterPhotoUrl }) => {
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image must be smaller than 6 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = dataUrl.split(",", 2)[1];
      onSetChoice(chapter.id, zone.zoneIndex, {
        action: "image",
        image_b64: b64,
        source: "upload",
        previewUrl: dataUrl,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleUseChapterPhoto = async () => {
    if (!chapterPhotoUrl) return;
    try {
      const fullUrl = chapterPhotoUrl.startsWith("/api/")
        ? `${BACKEND_URL}${chapterPhotoUrl}`
        : chapterPhotoUrl;
      const resp = await fetch(fullUrl);
      const blob = await resp.blob();
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        const b64 = dataUrl.split(",", 2)[1];
        onSetChoice(chapter.id, zone.zoneIndex, {
          action: "image",
          image_b64: b64,
          source: "chapter_photo",
          previewUrl: dataUrl,
        });
      };
      reader.readAsDataURL(blob);
    } catch {
      toast.error("Couldn't fetch chapter photo");
    }
  };

  const isSkipped = choice?.action === "skip";
  const isImaged = choice?.action === "image";

  return (
    <div
      className={`rounded-xl border p-3 transition-all ${
        isImaged ? "bg-emerald-500/10 border-emerald-500/40"
        : isSkipped ? "bg-white/5 border-white/10 opacity-60"
        : "bg-white/5 border-white/15"
      }`}
      data-testid={`zone-card-${chapter.id}-${zone.zoneIndex}`}
    >
      <div className="flex items-start gap-2 mb-3">
        <Camera className="w-3.5 h-3.5 text-amber-400/80 mt-0.5 flex-shrink-0" />
        <p className="font-sans text-[11px] text-white/70 italic leading-snug">"{zone.hint}"</p>
      </div>

      {isImaged ? (
        <div className="space-y-2">
          <div className="relative h-20 rounded-lg overflow-hidden bg-black/30">
            <img src={choice.previewUrl} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => onSetChoice(chapter.id, zone.zoneIndex, null)}
              className="absolute top-1 right-1 p-1 bg-black/70 rounded-full hover:bg-black"
              title="Remove image"
              data-testid={`zone-remove-${chapter.id}-${zone.zoneIndex}`}
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
          <p className="font-sans text-[10px] text-emerald-400 flex items-center gap-1">
            <Check className="w-3 h-3" />
            {choice.source === "chapter_photo" ? "Using chapter photo" : "Photo uploaded"}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-[11px] font-sans transition-colors"
            data-testid={`zone-upload-${chapter.id}-${zone.zoneIndex}`}
          >
            <Upload className="w-3 h-3" /> Upload Photo
          </button>
          {hasChapterPhoto && (
            <button
              onClick={handleUseChapterPhoto}
              className="flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-[11px] font-sans transition-colors"
              data-testid={`zone-use-chapter-${chapter.id}-${zone.zoneIndex}`}
            >
              <ImageIcon className="w-3 h-3" /> Use chapter photo
            </button>
          )}
          <button
            onClick={() => onSetChoice(chapter.id, zone.zoneIndex, { action: "skip" })}
            className="flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-transparent hover:bg-white/10 text-white/60 text-[11px] font-sans transition-colors"
            data-testid={`zone-skip-${chapter.id}-${zone.zoneIndex}`}
          >
            <X className="w-3 h-3" /> Skip
          </button>
        </div>
      )}
    </div>
  );
};

const PDFStudio = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const [margin, setMargin] = useState("standard");
  const [coverSource, setCoverSource] = useState("ai"); // 'ai' | 'upload' | 'none'
  const [coverUploadB64, setCoverUploadB64] = useState(null);
  const [coverUploadPreview, setCoverUploadPreview] = useState(null);
  const coverFileRef = useRef(null);

  // zoneChoices: { [chapterId]: { [zoneIndex]: {action, image_b64, source, previewUrl} } }
  const [zoneChoices, setZoneChoices] = useState({});

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/studio/${storyId}`);
      setBook(res.data.book);
      setChapters(res.data.chapters || []);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else toast.error("Failed to load book data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [storyId]);

  const setChoice = (chapterId, zoneIndex, choice) => {
    setZoneChoices((prev) => {
      const next = { ...prev };
      const chapter = { ...(next[chapterId] || {}) };
      if (choice === null) {
        delete chapter[zoneIndex];
      } else {
        chapter[zoneIndex] = choice;
      }
      next[chapterId] = chapter;
      return next;
    });
  };

  const handleSuggestZones = async () => {
    if (suggesting) return;
    setSuggesting(true);
    const tid = toast.loading("Asking AI for fresh image zones…");
    try {
      const res = await axios.post(`${API}/studio/${storyId}/suggest-image-zones`, {});
      // Refresh chapters from response
      if (res.data.chapters?.length) {
        setChapters((prev) => prev.map((ch) => {
          const updated = res.data.chapters.find((u) => u.id === ch.id);
          return updated ? { ...ch, content: updated.content } : ch;
        }));
        // Reset all choices since zone indices may have shifted
        setZoneChoices({});
      }
      toast.success("New image zones added!", { id: tid });
    } catch {
      toast.error("Failed to suggest zones", { id: tid });
    } finally {
      setSuggesting(false);
    }
  };

  const handleCoverUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Cover image must be smaller than 8 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setCoverUploadPreview(dataUrl);
      setCoverUploadB64(dataUrl.split(",", 2)[1]);
      setCoverSource("upload");
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    const tid = toast.loading("Building your magazine PDF…");

    // Open a placeholder tab inside the click handler so popup blockers don't block it.
    // We'll redirect it to the blob URL once the PDF is ready.
    let newTab = null;
    try { newTab = window.open("about:blank", "_blank"); } catch { /* ignore */ }

    try {
      // Build zones payload — convert choices map to per-chapter list (in zone order)
      const zonesPayload = {};
      chapters.forEach((ch) => {
        const parts = parseContent(ch.content);
        const totalZones = parts.filter(p => p.type === "zone").length;
        if (totalZones === 0) return;
        const list = [];
        for (let i = 0; i < totalZones; i++) {
          const c = zoneChoices[ch.id]?.[i];
          if (c?.action === "image" && c.image_b64) {
            list.push({ action: "image", image_b64: c.image_b64, source: c.source || "upload" });
          } else {
            list.push({ action: "skip" });
          }
        }
        zonesPayload[ch.id] = list;
      });

      const payload = { margin, cover_source: coverSource, zones: zonesPayload };
      if (coverSource === "upload" && coverUploadB64) {
        payload.cover_image_b64 = coverUploadB64;
      }

      const res = await axios.post(`${API}/studio/${storyId}/pdf/v2`, payload, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const filename = `${(book?.title || "Memoir").replace(/[^\w\s-]/g, "").trim()}.pdf`;

      // Redirect the pre-opened tab to the blob URL (works on iPad/Safari).
      if (newTab && !newTab.closed) {
        try { newTab.location.href = url; } catch { /* ignore */ }
      }

      // Desktop: also trigger a save-to-disk so the file lands in Downloads
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Don't revoke immediately — the new tab needs the blob URL alive
      setTimeout(() => URL.revokeObjectURL(url), 60_000);

      if (!newTab || newTab.closed) {
        // Pop-up was blocked — show a clickable toast that opens the PDF
        toast.success("PDF ready! Click to open.", {
          id: tid,
          duration: 12000,
          action: { label: "Open PDF", onClick: () => window.open(url, "_blank") },
        });
      } else {
        toast.success("PDF opened in a new tab! 📖", { id: tid });
      }
    } catch (e) {
      // Close the placeholder tab if generation failed
      if (newTab && !newTab.closed) { try { newTab.close(); } catch { /* ignore */ } }
      console.error(e);
      toast.error("PDF generation failed. Please try again.", { id: tid });
    } finally {
      setGenerating(false);
    }
  };

  const totalZones = useMemo(() => {
    return chapters.reduce((sum, ch) => sum + parseContent(ch.content).filter(p => p.type === "zone").length, 0);
  }, [chapters]);

  const filledZones = useMemo(() => {
    return Object.values(zoneChoices).reduce(
      (sum, ch) => sum + Object.values(ch).filter(c => c.action === "image").length,
      0
    );
  }, [zoneChoices]);

  if (loading) return (
    <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const aiCoverUrl = book?.cover_image
    ? (book.cover_image.startsWith("/api/") ? `${BACKEND_URL}${book.cover_image}` : book.cover_image)
    : null;
  const previewCoverUrl = coverSource === "upload" ? coverUploadPreview : (coverSource === "ai" ? aiCoverUrl : null);

  return (
    <div className="min-h-screen bg-[#1a1a2e]" data-testid="pdf-studio">
      <nav className="sticky top-0 z-50 bg-[#16162a]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-screen-2xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to={`/export/${storyId}`} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-to-export">
            <ArrowLeft className="w-4 h-4" />
            <Leaf className="w-5 h-5 text-[#7A9B7E]" />
            <span className="font-serif text-lg font-medium">PDF Studio</span>
            <span className="font-sans text-[10px] uppercase tracking-wider text-amber-400/80 ml-2">Magazine • Smart Blocks</span>
          </Link>
          <span className="text-xs font-sans text-amber-400/80 bg-amber-400/10 px-3 py-1 rounded-full" data-testid="launch-badge">Free Launch Offer</span>
        </div>
      </nav>

      <div className="flex h-[calc(100vh-57px)]">
        {/* ═══ LEFT PANEL — Controls + Zone cards ═══ */}
        <div className="w-[340px] flex-shrink-0 bg-[#16162a] border-r border-white/5 overflow-y-auto p-5 flex flex-col gap-5" data-testid="pdf-controls">

          {/* Zone progress */}
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <p className="font-sans text-[10px] uppercase tracking-wider text-white/50 mb-1">Smart Blocks</p>
            <p className="font-serif text-2xl text-white">{filledZones} <span className="text-white/30 text-base">/ {totalZones}</span></p>
            <p className="font-sans text-[11px] text-white/50">photos placed</p>
          </div>

          {/* Suggest zones */}
          <button
            onClick={handleSuggestZones}
            disabled={suggesting}
            className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-xs font-sans font-medium transition-colors disabled:opacity-60"
            data-testid="suggest-zones-btn"
          >
            <Wand2 className={`w-3.5 h-3.5 ${suggesting ? "animate-pulse" : ""}`} />
            {suggesting ? "Working…" : "Re-suggest image zones (AI)"}
          </button>

          {/* Margins */}
          <div>
            <p className="font-sans text-[10px] uppercase tracking-wider text-white/50 mb-2">Margin Style</p>
            <div className="grid grid-cols-3 gap-1.5" data-testid="margin-toggle">
              {Object.values(MARGIN_PRESETS).map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMargin(m.id)}
                  className={`py-2 rounded-lg text-[11px] font-sans transition-colors ${
                    margin === m.id ? "bg-[#7A9B7E] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
                  }`}
                  data-testid={`margin-${m.id}`}
                >
                  <div className="font-medium">{m.label}</div>
                  <div className="text-[9px] opacity-75">{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Cover source */}
          <div>
            <p className="font-sans text-[10px] uppercase tracking-wider text-white/50 mb-2">Cover Page</p>
            <div className="grid grid-cols-3 gap-1.5 mb-2" data-testid="cover-source">
              <button
                onClick={() => setCoverSource("ai")}
                disabled={!aiCoverUrl}
                className={`py-2 rounded-lg text-[11px] font-sans transition-colors disabled:opacity-30 ${
                  coverSource === "ai" ? "bg-[#7A9B7E] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
                data-testid="cover-source-ai"
              >
                AI Cover
              </button>
              <button
                onClick={() => coverFileRef.current?.click()}
                className={`py-2 rounded-lg text-[11px] font-sans transition-colors ${
                  coverSource === "upload" ? "bg-[#7A9B7E] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
                data-testid="cover-source-upload"
              >
                Upload
              </button>
              <button
                onClick={() => setCoverSource("none")}
                className={`py-2 rounded-lg text-[11px] font-sans transition-colors ${
                  coverSource === "none" ? "bg-[#7A9B7E] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
                data-testid="cover-source-none"
              >
                Text only
              </button>
            </div>
            <input ref={coverFileRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
            {coverSource === "upload" && !coverUploadPreview && (
              <p className="font-sans text-[10px] text-white/40 italic">Click "Upload" again to choose a file</p>
            )}
            {coverSource === "ai" && !aiCoverUrl && (
              <p className="font-sans text-[10px] text-amber-400/80">No AI cover yet — generate one from your Story Canvas first.</p>
            )}
          </div>

          {/* Zones grouped by chapter */}
          <div className="space-y-4 flex-1" data-testid="zones-list">
            {chapters.map((ch) => {
              const parts = parseContent(ch.content);
              const zones = parts.filter((p) => p.type === "zone");
              if (zones.length === 0) return null;
              return (
                <div key={ch.id}>
                  <p className="font-sans text-[11px] uppercase tracking-wider text-white/40 mb-2 truncate">
                    {ch.title || "Untitled"} <span className="text-white/30">({zones.length})</span>
                  </p>
                  <div className="space-y-2">
                    {zones.map((z) => (
                      <ZoneCard
                        key={z.zoneIndex}
                        chapter={ch}
                        zone={z}
                        choice={zoneChoices[ch.id]?.[z.zoneIndex]}
                        onSetChoice={setChoice}
                        hasChapterPhoto={!!ch.image_url}
                        chapterPhotoUrl={ch.image_url}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {totalZones === 0 && (
              <div className="rounded-xl bg-white/5 border border-dashed border-white/15 p-5 text-center" data-testid="no-zones-empty">
                <Sparkles className="w-6 h-6 text-amber-400/60 mx-auto mb-2" />
                <p className="font-sans text-xs text-white/70 mb-3">No image zones yet</p>
                <p className="font-sans text-[10px] text-white/40 leading-relaxed mb-3">Click "Re-suggest image zones" above to let AI propose 2–3 photo placements per chapter.</p>
              </div>
            )}
          </div>

          {/* Generate */}
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-[#1a1a2e] font-sans font-semibold text-sm transition-colors disabled:opacity-60 sticky bottom-0"
            data-testid="generate-pdf-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {generating ? "Generating…" : "Generate PDF"}
          </button>
        </div>

        {/* ═══ RIGHT PANEL — Live Preview ═══ */}
        <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-br from-[#1a1a2e] to-[#0e0e1c]" data-testid="pdf-preview">
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="font-sans text-xs uppercase tracking-wider text-white/40 text-center">Live Preview</p>

            {/* Cover preview */}
            <div
              className="bg-white shadow-2xl mx-auto relative overflow-hidden"
              style={{ width: "100%", aspectRatio: "8.5/11" }}
              data-testid="preview-cover"
            >
              {previewCoverUrl ? (
                <>
                  <img src={previewCoverUrl} alt="cover" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#9FB89F] to-[#5A7B5E]" />
              )}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-12">
                <h1 className="font-serif text-5xl font-bold text-white drop-shadow-lg mb-3">{book?.title || "My Story"}</h1>
                {book?.genre && <p className="font-sans text-sm text-white/90 italic">{book.genre}</p>}
              </div>
            </div>

            {/* Chapter previews */}
            {chapters.map((ch, idx) => {
              const parts = parseContent(ch.content);
              return (
                <div
                  key={ch.id}
                  className={`bg-white shadow-xl mx-auto ${MARGIN_PRESETS[margin].padding}`}
                  style={{ width: "100%", aspectRatio: "8.5/11", minHeight: "auto" }}
                  data-testid={`preview-chapter-${idx}`}
                >
                  <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#7A9B7E] mb-1">CHAPTER {idx + 1}</p>
                  <h2 className="font-serif text-3xl font-bold text-[#2D3A2D] mb-2">{ch.title || "Untitled"}</h2>
                  {ch.time_period && <p className="font-sans text-xs italic text-[#7A8A7A] mb-5">{ch.time_period}</p>}

                  <div className="font-serif text-[12px] leading-[1.6] text-[#2D3A2D] text-justify space-y-3">
                    {parts.map((p, pi) => {
                      if (p.type === "text") {
                        return <p key={pi} className="indent-4 first:indent-0">{p.text}</p>;
                      }
                      const choice = zoneChoices[ch.id]?.[p.zoneIndex];
                      if (choice?.action === "image" && choice.previewUrl) {
                        const side = pi % 2 === 0 ? "float-left mr-4" : "float-right ml-4";
                        return (
                          <img
                            key={pi}
                            src={choice.previewUrl}
                            alt=""
                            className={`${side} mb-2 w-1/3 object-cover rounded-md`}
                            data-testid={`preview-zone-image-${ch.id}-${p.zoneIndex}`}
                          />
                        );
                      }
                      if (choice?.action === "skip") {
                        // No DOM at all — preview text collapses naturally
                        return null;
                      }
                      // Unset zone — show the hint as a dashed placeholder so user sees what AI suggested
                      return (
                        <div
                          key={pi}
                          className="my-3 mx-auto w-2/3 border-2 border-dashed border-amber-400/40 bg-amber-50/40 rounded-lg p-3 text-center"
                          data-testid={`preview-zone-placeholder-${ch.id}-${p.zoneIndex}`}
                        >
                          <Camera className="w-4 h-4 text-amber-500/70 mx-auto mb-1" />
                          <p className="font-sans text-[10px] text-amber-700/80 italic">"{p.hint}"</p>
                          <p className="font-sans text-[9px] text-amber-600/60 mt-0.5">Choose Upload or Skip in the left panel</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFStudio;
